"use client";

import { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import LandingTerminalLink from "./LandingTerminalLink";
import "./HeroSection.css";

function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let holdPause = reducedMotion;

    const tryPlay = () => {
      if (holdPause) return;
      const playAttempt = video.play();
      if (playAttempt) playAttempt.catch(() => {});
    };

    const restart = () => {
      try {
        video.currentTime = 0;
      } catch {
        /* Safari can throw if metadata is not ready */
      }
      tryPlay();
    };

    const onPause = () => {
      if (holdPause || document.hidden || video.ended) return;
      tryPlay();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") tryPlay();
    };

    video.addEventListener("ended", restart);
    video.addEventListener("stalled", tryPlay);
    video.addEventListener("suspend", tryPlay);
    video.addEventListener("pause", onPause);
    document.addEventListener("visibilitychange", onVisibility);

    const observer = new IntersectionObserver(
      ([entry]) => {
        holdPause = reducedMotion || !entry.isIntersecting;
        if (holdPause) video.pause();
        else tryPlay();
      },
      { threshold: 0.15 },
    );
    observer.observe(video);

    tryPlay();

    return () => {
      observer.disconnect();
      video.removeEventListener("ended", restart);
      video.removeEventListener("stalled", tryPlay);
      video.removeEventListener("suspend", tryPlay);
      video.removeEventListener("pause", onPause);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <video
      ref={videoRef}
      className="landing-hero__video"
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      disablePictureInPicture
      disableRemotePlayback
    >
      <source src="/hero-terminal.mp4" type="video/mp4" />
    </video>
  );
}

export default function HeroSection() {
  return (
    <section className="landing-hero">
      <div className="landing-hero__inner">
        <div className="landing-hero__copy">
          <div className="landing-hero__eyebrow">
            For investors who do their own research
          </div>
          <h1>Check a stock before you buy it.</h1>
          <p>
            Find out if the numbers back your investment idea. A
            plain-English verdict in a minute, on any of 4,000+ stocks.
          </p>

          <div className="landing-hero__actions">
            <LandingTerminalLink href="/hub" className="landing-hero__primary">
              Explore Terminal
            </LandingTerminalLink>
            <a href="#method" className="landing-hero__secondary">
              See How Bastion Works
              <ArrowRight size={15} strokeWidth={1.8} />
            </a>
          </div>
        </div>

        <div className="landing-hero__media" aria-label="Bastion terminal in action">
          <HeroVideo />
        </div>
      </div>
    </section>
  );
}
